import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import type {
  InviteModalStoreProps,
  InviteModalStoreState,
} from '../types/use-invite-modal-store-types';

// Helper function to validate accountId
const isValidAccountId = (accountId: string | null | undefined): accountId is string => {
  return Boolean(accountId && typeof accountId === 'string' && accountId.trim());
};


const initialState: InviteModalStoreState = {
  shownModals: {},
};

export const useInviteModalStore = create<InviteModalStoreProps>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        hasShownModal: (accountId) => {
          if (!isValidAccountId(accountId)) return true; // Prevent modal if invalid ID

          try {
            return get().shownModals[accountId] === true;
          } catch {
            return true; // Prevent modal on error
          }
        },

        markModalAsShown: (accountId) => {
          if (!isValidAccountId(accountId)) return;

          try {
            set(
              (state) => ({
                shownModals: {
                  ...state.shownModals,
                  [accountId]: true,
                },
              }),
              false,
              'MARK_INVITE_MODAL_AS_SHOWN'
            );
          } catch (error) {
            console.error('Failed to mark modal as shown:', error);
          }
        },

        resetAllModals: () => {
          try {
            set(() => ({ shownModals: {} }), false, 'RESET_ALL_INVITE_MODALS');
          } catch (error) {
            console.error('Failed to reset modals:', error);
          }
        },

        destroy: () => {
          try {
            set(() => ({ ...initialState }), false, 'CLEAR_INVITE_MODAL_STORE');
            localStorage.removeItem('@upstack/invite-modal');
          } catch (error) {
            console.error('Failed to destroy invite modal store:', error);
          }
        },
      }),
      {
        name: '@upstack/invite-modal',
        onRehydrateStorage: () => (state, error) => {
          if (error) console.warn('Failed to load invite modal data:', error);
        },
      }
    ),
    { enabled: process.env.NODE_ENV === 'development' }
  )
);
